import { useState } from "react";
import { rfpApi } from "@/lib/api-client";
import { toast } from "sonner";
import { FileSignature, Archive, Loader2, PenLine, ThumbsUp, ThumbsDown, FileDown } from "lucide-react";

const STATUS_CLS = {
  pending: "border-amber-500/50 text-amber-400",
  founder_signed: "border-sky-500/50 text-sky-300",
  dispatch_ready: "border-violet-500/50 text-violet-300",
  submitted: "border-emerald-500/50 text-emerald-400",
  rejected: "border-white/20 text-white/40",
};

export const SovereignRFPCard = ({ rfp: initial, onSigned, onOutcome }) => {
  const [rfp, setRfp] = useState(initial);
  const [busy, setBusy] = useState(null);
  const [sigName, setSigName] = useState("");

  const sign = async () => {
    if (!sigName.trim()) { toast.error("Founder signature required"); return; }
    setBusy("sign");
    try {
      const r = await rfpApi.sign(rfp.id, sigName.trim());
      setRfp(r.rfp);
      toast.success(`SEQUENCE LOCK RELEASED — RFQ ${r.rfq.id.slice(0, 8)} raised. Authorize it in the RFQ tab.`);
      onSigned?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Founder-Sign failed");
    } finally { setBusy(null); }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      await rfpApi.reject(rfp.id);
      setRfp((x) => ({ ...x, status: "rejected" }));
      toast.info("RFP rejected — opportunity archived. No RFQ was generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
    } finally { setBusy(null); }
  };

  const outcome = async (bite) => {
    setBusy("outcome");
    try {
      await rfpApi.outcome(rfp.id, bite);
      setRfp((x) => ({ ...x, outcome: bite ? "bite" : "no_bite" }));
      onOutcome?.();
      toast.success(bite ? "BITE recorded — the Win-Rate Monitor now favors this style."
                         : "No bite recorded — the Proposal Engine will adapt.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Outcome update failed");
    } finally { setBusy(null); }
  };

  const job = rfp.job || {};
  const pending = rfp.status === "pending";
  const signed = ["founder_signed", "dispatch_ready", "submitted"].includes(rfp.status);
  return (
    <div className="border border-sky-500/30 bg-black/60 p-3" data-testid="rfp-card">
      <div className="flex items-center gap-2 flex-wrap">
        <FileSignature size={11} className="text-sky-300 flex-none" />
        <span className="font-mono-uc text-[9px] uppercase tracking-widest text-sky-300">Sovereign RFP</span>
        <span data-testid="rfp-status"
              className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_CLS[rfp.status] || STATUS_CLS.pending}`}>
          {rfp.status}
        </span>
        <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-white/60"
              data-testid="rfp-platform">{job.platform || "?"}</span>
        <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/15 text-white/50"
              data-testid="rfp-style">{rfp.style} style</span>
        {rfp.outcome && (
          <span data-testid="rfp-outcome-badge"
                className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${rfp.outcome === "bite" ? "border-emerald-500/50 text-emerald-400" : "border-rose-500/40 text-rose-300"}`}>
            {rfp.outcome === "bite" ? "BITE" : "NO BITE"}
          </span>
        )}
        <span className="ml-auto font-mono-uc text-[11px] text-emerald-400" data-testid="rfp-budget">
          ${Number(job.budget || 0).toFixed(2)}
        </span>
      </div>

      {/* Job Description */}
      <div className="mt-2 border border-white/10 px-2 py-1.5">
        <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40">Job Description</div>
        <div className="text-[11px] text-white/85 mt-0.5" data-testid="rfp-job-title">{job.title}</div>
      </div>

      {/* Oracle-Drafted Proposal */}
      <div className="mt-2 border border-sky-500/20 bg-sky-500/[0.03] px-2 py-1.5">
        <div className="font-mono-uc text-[8px] uppercase tracking-widest text-sky-300/80">
          Oracle Proposal · {rfp.engine === "oracle" ? "bespoke draft" : "engine offline"}
        </div>
        <div className="text-[11px] text-white/80 mt-1 whitespace-pre-wrap max-h-44 overflow-y-auto uc-scroll"
             data-testid="rfp-proposal-text">{rfp.proposal_text}</div>
      </div>

      {rfp.signature && (
        <div className="font-mono-uc text-[9px] text-sky-300 mt-1.5" data-testid="rfp-signature-note">
          Founder-Signed by {rfp.signature.name} · {rfp.signature.signed_at}
          {rfp.rfq_id ? ` · RFQ ${rfp.rfq_id.slice(0, 8)} raised` : ""}
        </div>
      )}
      {rfp.dispatch_state && (
        <div className="font-mono-uc text-[9px] text-violet-300 mt-1" data-testid="rfp-dispatch-state">
          Dispatch: {rfp.dispatch_state}
        </div>
      )}

      {pending && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <input data-testid="rfp-signature-input" value={sigName}
                 onChange={(e) => setSigName(e.target.value)} placeholder="Founder signature (full name)"
                 className="w-52 bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white/90 focus:border-sky-500/50 outline-none" />
          <button data-testid="rfp-sign-btn" onClick={sign} disabled={!!busy}
                  className="px-3 py-1.5 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
            {busy === "sign" ? <Loader2 size={10} className="animate-spin" /> : <PenLine size={10} />} Founder-Sign → Raise RFQ
          </button>
          <button data-testid="rfp-reject-btn" onClick={reject} disabled={!!busy}
                  className="px-3 py-1.5 border border-white/15 text-white/50 hover:border-rose-500/40 hover:text-rose-300 font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
            {busy === "reject" ? <Loader2 size={10} className="animate-spin" /> : <Archive size={10} />} Reject
          </button>
        </div>
      )}

      {signed && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {rfp.packet_download && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${rfp.packet_download}`} target="_blank" rel="noreferrer"
               data-testid="rfp-packet-link"
               className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-1 border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 flex items-center gap-1">
              <FileDown size={9} /> Proposal Packet ↓
            </a>
          )}
          {!rfp.outcome && (
            <>
              <button data-testid="rfp-bite-btn" onClick={() => outcome(true)} disabled={!!busy}
                      className="px-2 py-1 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 font-mono-uc text-[8px] uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
                <ThumbsUp size={9} /> Bite
              </button>
              <button data-testid="rfp-no-bite-btn" onClick={() => outcome(false)} disabled={!!busy}
                      className="px-2 py-1 border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 font-mono-uc text-[8px] uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
                <ThumbsDown size={9} /> No Bite
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
